// Immutable dot-path helpers + value coercion, ported from the legacy wizard.
// Kept dependency-free so they can be unit tested directly.

// General-purpose truthiness for drafts: keeps booleans/0, recurses objects and
// arrays, and treats exact "N/A" as empty. logic/idValidation.ts keeps a
// stricter string-only variant (trim + case-insensitive "N/A") on purpose; do
// not collapse the two.
export function hasValue(value: unknown): boolean {
  if (value === false || value === true || value === 0) return true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "" && value !== "N/A";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(hasValue);
  }
  return true;
}

export function getPath<T = unknown>(source: unknown, path: string): T | undefined {
  return path.split(".").reduce<unknown>((value, key) => {
    if (value && typeof value === "object") {
      return (value as Record<string, unknown>)[key];
    }
    return undefined;
  }, source) as T | undefined;
}

export function setPath<T extends Record<string, unknown>>(
  source: T,
  path: string,
  value: unknown
): T {
  const keys = path.split(".");
  const next: Record<string, unknown> = { ...source };
  let cursor = next;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    cursor[key] = { ...((cursor[key] as Record<string, unknown>) || {}) };
    cursor = cursor[key] as Record<string, unknown>;
  });

  return next as T;
}

export function deletePath<T extends Record<string, unknown>>(
  source: T,
  path: string
): T {
  const keys = path.split(".");
  const next: Record<string, unknown> = { ...source };
  let cursor: Record<string, unknown> = next;

  keys.forEach((key, index) => {
    if (!cursor || typeof cursor !== "object") return;
    if (index === keys.length - 1) {
      delete cursor[key];
      return;
    }
    cursor[key] = { ...((cursor[key] as Record<string, unknown>) || {}) };
    cursor = cursor[key] as Record<string, unknown>;
  });

  return next as T;
}

// Recursively drops undefined/null and prunes empty objects/arrays so the
// submit payload never carries explicit null (the API rejects null fields).
export function compactObject(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(compactObject).filter((item) => item !== undefined);
  }
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, compactObject(item)] as const)
      .filter(([, item]) => item !== undefined);
    if (entries.length === 0) return undefined;
    return Object.fromEntries(entries);
  }
  if (value === null || value === undefined) return undefined;
  return value;
}

export function isYes(value: unknown): boolean {
  return value === true || value === "1" || value === 1 || value === "yes";
}

export function isNo(value: unknown): boolean {
  return value === false || value === "0" || value === 0 || value === "no";
}

export function booleanSelectValue(value: unknown): boolean | "" {
  if (isYes(value)) return true;
  if (isNo(value)) return false;
  return "";
}
